/* Resolve preview: pure model. The request body and the preview column of an effective variable. */
import type { App, Environment, EnvVisibility, Platform } from '@hangar/shared';
import type { EnvResolveInput } from '@/api/endpoints';

/** "A, B  C" → ['A', 'B', 'C']: comma and whitespace separated, empties dropped. */
export function parseCiVariables(text: string): string[] {
  return text.split(/[,\s]+/).map((s) => s.trim()).filter(Boolean);
}

export function resolveBody(environment: string, platform: Platform, ciVariablesText: string): EnvResolveInput {
  const ci = parseCiVariables(ciVariablesText);
  return { environment, platform, ...(ci.length ? { ciVariables: ci } : {}) };
}

/** The tooltip of the preview column: only plain values have a preview worth showing in full. */
export function previewTitle(visibility: EnvVisibility, preview: string | null | undefined): string | undefined {
  return visibility === 'plain' ? (preview ?? undefined) : undefined;
}

/** The preview column text; secrets are masked and a missing preview is a dash. */
export function previewText(visibility: EnvVisibility, preview: string | null | undefined): { masked: boolean; text: string } {
  if (visibility === 'secret') return { masked: true, text: '••••••' };
  return { masked: false, text: preview ?? '—' };
}

interface InitialInput {
  apps: App[] | undefined;
  environments: Environment[];
  initialAppSlug?: string;
  initialEnvironment?: string;
  initialPlatform?: Platform;
}

/** First selections: what the page was opened with, else the first app, the first environment and Android. */
export function initialResolveSelection(i: InitialInput): { appSlug: string; environment: string; platform: Platform } {
  return {
    appSlug: i.initialAppSlug ?? i.apps?.[0]?.slug ?? '',
    environment: i.initialEnvironment ?? i.environments[0]?.slug ?? '',
    platform: i.initialPlatform ?? 'android',
  };
}
